import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { readPreference, savePreference } from "@/lib/tabPreferences";
import { applyChatEvent, streamChat } from "./chatStream";
import { useChatConversation } from "./useChatConversation";
export { stripTags } from "./chatStream";

const MODELS = ["chatgpt", "gemini", "claude"];
export const useAiChat = (cartIds) => {
  const { user, loading } = useAuth();
  const owner = user?.user_id || "guest";
  const { messages, setMessages, runtime, ensureSession, reset: resetSession } = useChatConversation(owner, loading);
  const [streaming, setStreaming] = useState(false);
  const [model, setModel] = useState(() => { const saved = readPreference("kandula-ai-model"); return MODELS.includes(saved) ? saved : "chatgpt"; });
  const queue = useRef([]), working = useRef(false);
  useEffect(() => { queue.current = []; working.current = false; setStreaming(false); }, [owner, loading]);

  const send = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    queue.current.push({ text, model, cartIds });
    if (working.current) return;
    working.current = true; setStreaming(true);
    const state = runtime.current, generation = state.generation;
    try {
      while (queue.current.length && generation === state.generation) {
        const request = queue.current.shift(), replyId = crypto.randomUUID();
        let appended = false;
        try {
          const sessionId = await ensureSession();
          if (!sessionId || generation !== state.generation) break;
          setMessages((old) => [...old, { id: crypto.randomUUID(), role: "user", text: request.text },
            { id: replyId, role: "assistant", text: "", products: [], pending: true, model: request.model }]);
          appended = true; state.controller = new AbortController();
          await streamChat({ session_id: sessionId, message: request.text, cart_ids: request.cartIds, model: request.model }, state.controller.signal, (event) => {
            if (generation === state.generation) setMessages((old) => old.map((m) => m.id === replyId ? applyChatEvent(m, event) : m));
          });
        } catch (err) {
          if (generation !== state.generation) break;
          const error = { id: replyId, role: "assistant", text: err.message || "Chat is unavailable. Please try again.", products: [], error: true, model: request.model };
          setMessages((old) => appended ? old.map((m) => m.id === replyId ? error : m) : [...old, error]);
        }
      }
    } finally { if (generation === state.generation) { working.current = false; setStreaming(false); } }
  }, [cartIds, ensureSession, loading, model, runtime, setMessages]);
  const reset = useCallback(() => { queue.current = []; working.current = false; setStreaming(false); resetSession(); }, [resetSession]);
  const changeModel = useCallback((choice) => {
    if (working.current || !MODELS.includes(choice)) return;
    setModel(choice); savePreference("kandula-ai-model", choice);
  }, []);
  return { messages, send, streaming, reset, model, changeModel, ensureSession };
};